// src/utils/prompts.js
// Centraliza AQUÍ todo tu texto repetitivo y construcción de prompts ↓↓↓

/* ---------- ESQUEMA DE BASE DE DATOS REAL ---------- */
export const DB_SCHEMA = `
ESQUEMA DE BASE DE DATOS:

public.ventas_medico (
  "fecha" TEXT,                         -- Fecha en formato AAAA-MM
  "tipo_pac2" TEXT,                     -- Tipo de paciente (externos, internos, etc.)
  "clase_episodio" BIGINT,              -- Clasificación del episodio médico
  "centro_sanitario" TEXT,              -- Código del centro sanitario
  "id_medico" TEXT,                     -- Identificador único del médico
  "nombre_medico" TEXT,                 -- Nombre completo del profesional
  "descripcion_especialidad" TEXT,      -- Especialidad médica
  "estudios" BIGINT,                    -- Cantidad de estudios/procedimientos realizados
  "importe" DOUBLE PRECISION            -- Facturación generada
);

public.ventas_sucursal (
  "fecha" TEXT,                         -- Fecha en formato AAAA-MM
  "id_prestacion" TEXT,                 -- Código de la prestación médica
  "centro_sanitario" TEXT,              -- Código del centro sanitario
  "descripcion_posicion" TEXT,          -- Descripción del procedimiento/prestación
  "estudios" TEXT,                      -- Cantidad de estudios realizados
  "importe" TEXT                        -- Facturación generada
);

RELACIONES PRINCIPALES:
- Dos tablas complementarias del sistema hospitalario
- ventas_medico: Análisis por profesional médico y especialidad
- ventas_sucursal: Análisis por prestación médica y centro sanitario

IMPORTANTE - TIPOS DE DATOS:
- "fecha": Es TEXT en formato 'AAAA-MM', usar: "fecha" = '2024-01' o "fecha" LIKE '2024%'
- "estudios": BIGINT en ventas_medico, TEXT en ventas_sucursal (convertir si necesario)
- "importe": DOUBLE PRECISION en ventas_medico, TEXT en ventas_sucursal (convertir si necesario)
- Todos los campos de texto: usar ILIKE para búsquedas flexibles
- IMPORTANTE: Todos los nombres de columna van entre comillas dobles por contener espacios

NOTAS CRÍTICAS PARA FECHAS Y FILTROS:
- Para filtrar por año: WHERE "fecha" LIKE '2024%' o WHERE "fecha" >= '2024-01'
- Para filtrar por mes específico: WHERE "fecha" = '2024-08'
- Para rangos de fechas: WHERE "fecha" BETWEEN '2024-01' AND '2024-12'
- Para análisis temporal: ORDER BY "fecha" DESC
- Para búsquedas flexibles en especialidades: WHERE "descripcion_especialidad" ILIKE '%cardiolog%'
- Para análisis de productividad: usar SUM("estudios") y SUM("importe")
`;

/* ---------- BUILDERS DE PROMPTS ---------- */
export const buildWebSearchPrompt = (userQuery) => `
Eres un asistente especializado en búsqueda web que complementa a un sistema de análisis del sector salud mexicano. Tu función es ÚNICAMENTE buscar información externa específica que NO está disponible en la base de datos hospitalaria.

ANÁLISIS DE LA CONSULTA:
Primero analiza la pregunta del usuario e identifica QUÉ información específica necesita ser buscada en internet vs qué puede ser respondido con datos hospitalarios internos.

INFORMACIÓN QUE DEBES BUSCAR (externa/actualizada):
- Tarifas actuales de procedimientos médicos en el mercado
- Nuevos tratamientos y procedimientos médicos
- Noticias del sector salud mexicano
- Cambios en regulaciones sanitarias (NOM, COFEPRIS, etc.)
- Comparaciones internacionales del sector salud
- Tendencias globales en medicina y tecnología médica
- Datos de otros hospitales para benchmarking
- Información sobre especialistas y certificaciones médicas
- Políticas gubernamentales que afecten el sector salud

INFORMACIÓN QUE NO DEBES BUSCAR (disponible internamente):
- Productividad histórica por médico/especialidad
- Facturación por centro sanitario
- Datos de estudios realizados por tipo de paciente
- Estadísticas de procedimientos médicos internos
- Tendencias temporales de actividad hospitalaria
- Análisis por especialidad médica interna

INSTRUCCIONES:
1. Si la pregunta SOLO requiere datos internos → Responde: "No se requiere búsqueda web. El agente principal puede responder con datos hospitalarios internos."

2. Si la pregunta requiere información externa específica → Busca ÚNICAMENTE esa información y proporciona:
   - Datos específicos encontrados (números, fechas, valores exactos)
   - Fuente y fecha de la información
   - Contexto breve si es relevante

3. Si no encuentras la información específica → Responde: "No se pudo encontrar información actualizada sobre [tema específico]. El agente principal debe proceder solo con datos internos."

FORMATO DE RESPUESTA:
Si encuentras información:
"INFORMACIÓN EXTERNA ENCONTRADA:
[Dato específico]: [Valor] ([Fuente], [Fecha])
[Contexto breve si es necesario]"

Sé preciso, conciso y enfócate solo en la información externa necesaria, sé lo más breve posible sólo contesta lo que se te pide.

CONSULTA DEL USUARIO: ${userQuery}
`;

export const buildSuggestedQuestionsPrompt = (prevQ, botResp) => `
Estoy usando un asistente de análisis del sector salud con datos hospitalarios en PostgreSQL. Por favor, genera 3 preguntas de seguimiento relevantes basadas en la pregunta anterior y la respuesta obtenida. 
             
${DB_SCHEMA}

Pregunta anterior: "${prevQ}"
Respuesta: "${botResp}"

Instrucciones:
1. Las preguntas deben ser para profundizar en el análisis médico/hospitalario y descubrir insights adicionales
2. Deben ser específicas y relacionadas con productividad médica, especialidades, centros sanitarios
3. Deben poder responderse con las tablas ventas_medico y ventas_sucursal disponibles
4. Deben enfocarse en análisis como productividad por médico, tendencias temporales, comparaciones por especialidad/centro
5. Deben ser breves y estar listas para copiar y pegar

IMPORTANTE: Responde SOLO con las 3 preguntas en formato estrictamente JSON como el siguiente:
["pregunta 1", "pregunta 2", "pregunta 3"]

No incluyas otra explicación, solo el array JSON.
`;

export const buildConsultClaudePrompt = (origQuery, errorMsg) => `
Eres un asistente especializado en análisis del sector salud. El usuario está intentando 
obtener información de una base de datos PostgreSQL con datos hospitalarios con el siguiente esquema:

${DB_SCHEMA}

Ha ocurrido un error con la consulta:
"${origQuery}"
Error devuelto:
"${errorMsg}"

Por favor, proporciona 2-3 alternativas para reformular mejor esta consulta médica. 
Para cada alternativa, proporciona:
1. Una pregunta reformulada más específica y clara sobre análisis hospitalario
2. Una breve justificación de por qué esta alternativa podría funcionar mejor

Devuelve solo las alternativas en formato de lista numerada, sin introducción ni conclusión.
Cada alternativa debe ser concisa y directa, enfocándose en obtener datos relevantes del sector salud que el sistema pueda procesar.
`;

export const buildSQLPrompt = (userContent, mensajesJSON) => { return `
Eres un agente especializado en optimizar consultas SQL para PostgreSQL basado en análisis del sector salud con datos hospitalarios, principalmente estas consultas son para generar tablas de excel con los datos.

${DB_SCHEMA}

Consulta del usuario:
${userContent}

Consultas de referencia:
${mensajesJSON}

⚠️ REGLAS CRÍTICAS PARA GARANTIZAR RESULTADOS CON DATOS:

🔴 **REGLAS DE EXISTENCIA DE DATOS (CRÍTICAS)**:

1. **BÚSQUEDAS FLEXIBLES EN TEXTO**:
   - ✅ USAR: "descripcion_especialidad" ILIKE '%cardiolog%' (busca "cardiología", "Cardiología", etc.)
   - ❌ EVITAR: "descripcion_especialidad" = 'Cardiología' (solo encuentra coincidencias exactas)
   - ✅ USAR: "nombre_medico" ILIKE '%juan%' OR "nombre_medico" ILIKE '%maria%'
   - ✅ USAR: UPPER("centro_sanitario") LIKE UPPER('%cs%') para mayor flexibilidad

2. **FILTROS NUMÉRICOS INCLUSIVOS**:
   - ✅ USAR: "estudios" >= 0 (incluye registros con 0 estudios)
   - ❌ EVITAR: "estudios" > 0 (excluye registros importantes con 0 estudios)
   - ✅ USAR: "importe" >= 0 (incluye registros sin facturación)

3. **PERÍODOS DE TIEMPO AMPLIOS**:
   - ✅ POR DEFECTO: "fecha" >= '2024-01' (año completo)
   - ✅ PARA ANÁLISIS DETALLADO: "fecha" >= '2023-01' (dos años)
   - ❌ EVITAR: "fecha" = '2024-08' (muy restrictivo)
   - ✅ USAR: "fecha" BETWEEN '2024-01' AND '2024-12' para rangos específicos

4. **HAVING CLAUSES FLEXIBLES**:
   - ✅ USAR: HAVING COUNT(*) > 0 (cualquier registro)
   - ✅ USAR: HAVING SUM(CAST("estudios" AS NUMERIC)) >= 0
   - ❌ EVITAR: HAVING COUNT(*) > 10 o SUM("estudios") > 100 (muy restrictivo)

5. **ESTRATEGIA DE VERIFICACIÓN PROGRESIVA**:
   - PASO 1: Usar filtros mínimos primero (solo fecha e IS NOT NULL)
   - PASO 2: Agregar filtros de especialidad/centro gradualmente
   - PASO 3: Si no hay datos, expandir criterios automáticamente

⚠️ REGLAS CRÍTICAS DE OPTIMIZACIÓN Y RENDIMIENTO:

1. **FILTROS TEMPORALES OBLIGATORIOS**: 
   - SIEMPRE incluir filtros de fecha para evitar procesar años completos
   - Por defecto usar: WHERE "fecha" >= '2024-01'
   - Para análisis específicos: WHERE "fecha" >= '2023-01'
   - Para tendencias largas: WHERE "fecha" >= '2022-01'

2. **FILTROS DE CALIDAD DE DATOS FLEXIBLES**:
   - SIEMPRE filtrar valores nulos: WHERE "nombre_medico" IS NOT NULL, WHERE "descripcion_especialidad" IS NOT NULL
   - Para campos numéricos: WHERE "estudios" >= 0, WHERE "importe" >= 0
   - Para análisis de productividad: WHERE "estudios" >= 0 AND "nombre_medico" IS NOT NULL

3. **LÍMITES DE RESULTADOS INTELIGENTES**:
   - Para análisis generales: LIMIT 2000 (incrementado)
   - Para análisis detallados: LIMIT 5000
   - Para exports completos: LIMIT 10000 (máximo absoluto)
   - Para TOP rankings: LIMIT 100 o LIMIT 200

4. **CONVERSIÓN DE TIPOS SEGURA**:
   - Para ventas_sucursal: CAST("estudios" AS INTEGER), CAST("importe" AS NUMERIC)
   - Para operaciones: CAST("estudios" AS NUMERIC), CAST("importe" AS NUMERIC)
   - Usar NULLIF para divisiones: NULLIF(CAST("importe" AS NUMERIC), 0)

5. **AGRUPACIONES EFICIENTES**:
   - Usar HAVING para filtrar grupos: HAVING COUNT(*) >= 0, HAVING SUM("estudios") >= 0
   - Agrupar por campos apropiados: "nombre_medico", "descripcion_especialidad", "centro_sanitario"
   - Evitar GROUP BY con campos de alta cardinalidad sin otros filtros

6. **CTEs (Common Table Expressions) OPTIMIZADOS**:
   - Cada CTE debe tener sus propios filtros temporales y de calidad
   - Evitar CTEs que devuelvan millones de registros
   - Usar filtros específicos en cada CTE antes de hacer agregaciones

7. **CAMPOS CALCULADOS EFICIENTES**:
   - Para divisiones: NULLIF(denominador, 0) siempre
   - Para ROUND: ROUND(CAST("importe" AS NUMERIC), 2) 
   - Para agregaciones: usar HAVING para filtrar resultados nulos

🔴 **REGLAS ESPECIALES PARA DATOS MÉDICOS INCONSISTENTES**:

1. **MANEJO DE ESPECIALIDADES INCONSISTENTES**:
   - Usar múltiples variaciones: "descripcion_especialidad" ILIKE '%cardio%' OR "descripcion_especialidad" ILIKE '%corazon%'
   - Incluir sinónimos: "descripcion_especialidad" ILIKE '%gineco%' OR "descripcion_especialidad" ILIKE '%obstet%'

2. **MANEJO DE CENTROS SANITARIOS**:
   - Buscar patrones: "centro_sanitario" ILIKE '%CS%' OR "centro_sanitario" ILIKE '%hospital%'
   - Usar códigos flexibles: "centro_sanitario" ILIKE '%001%' para encontrar variaciones

3. **VERIFICACIÓN DE EXISTENCIA PREVIA**:
   - Incluir COUNT DISTINCT para verificar variedad de datos
   - Mostrar ejemplos de valores únicos en campos de texto

IMPORTANTES REGLAS PARA FECHAS Y PERÍODOS (CRÍTICO):
1. La columna "fecha" es TEXT en formato 'AAAA-MM', usar: "fecha" = '2024-08' o "fecha" >= '2024-01'
2. Para filtros por año: WHERE "fecha" LIKE '2024%' o WHERE "fecha" >= '2024-01'
3. Para filtros por mes específico: WHERE "fecha" = '2024-08'
4. Para rangos: WHERE "fecha" BETWEEN '2024-01' AND '2024-12'
5. Para análisis estacionales: 
   - Primer trimestre: WHERE "fecha" IN ('2024-01', '2024-02', '2024-03')
   - Segundo trimestre: WHERE "fecha" IN ('2024-04', '2024-05', '2024-06')
   - Tercer trimestre: WHERE "fecha" IN ('2024-07', '2024-08', '2024-09')
   - Cuarto trimestre: WHERE "fecha" IN ('2024-10', '2024-11', '2024-12')

EJEMPLOS DE CONSULTAS QUE GARANTIZAN DATOS:

✅ **CONSULTA QUE SIEMPRE TRAE DATOS** (patrón a seguir):
"
SELECT 
    "fecha",
    "nombre_medico",
    "descripcion_especialidad",
    "centro_sanitario",
    SUM(CAST("estudios" AS NUMERIC)) AS total_estudios,
    SUM(CAST("importe" AS NUMERIC)) AS total_facturacion,
    COUNT(*) AS registros,
    ROUND(AVG(CAST("estudios" AS NUMERIC)), 2) AS promedio_estudios
FROM ventas_medico 
WHERE "fecha" >= '2024-01'                    -- Período amplio
  AND "descripcion_especialidad" ILIKE '%cardio%'         -- Búsqueda flexible
  AND "estudios" >= 0                  -- Incluye ceros
  AND "nombre_medico" IS NOT NULL
  AND "descripcion_especialidad" IS NOT NULL
GROUP BY "fecha", "nombre_medico", "descripcion_especialidad", "centro_sanitario"
HAVING COUNT(*) > 0                    -- Muy flexible
ORDER BY "fecha" DESC, total_estudios DESC
LIMIT 2000;
"

✅ **CONSULTA DE PRODUCTIVIDAD POR ESPECIALIDAD** (patrón recomendado):
"
WITH total_sistema AS (
    SELECT SUM(CAST("estudios" AS NUMERIC)) AS total_estudios_sistema
    FROM ventas_medico 
    WHERE "fecha" >= '2024-01' 
      AND "estudios" >= 0
      AND "descripcion_especialidad" IS NOT NULL
)
SELECT 
    "descripcion_especialidad",
    "centro_sanitario",
    SUM(CAST("estudios" AS NUMERIC)) AS estudios_especialidad,
    ROUND(
        (SUM(CAST("estudios" AS NUMERIC)) * 100.0 / 
         NULLIF((SELECT total_estudios_sistema FROM total_sistema), 0)), 2
    ) AS porcentaje_productividad
FROM ventas_medico 
WHERE "fecha" >= '2024-01'
  AND "estudios" >= 0
  AND "descripcion_especialidad" IS NOT NULL
GROUP BY "descripcion_especialidad", "centro_sanitario"
ORDER BY estudios_especialidad DESC
LIMIT 1000;
"

❌ **CONSULTA RESTRICTIVA** (evitar este patrón):
"
SELECT "nombre_medico", COUNT(*)
FROM ventas_medico
WHERE "nombre_medico" = 'Dr. Juan Pérez'               -- Muy específico
  AND "estudios" > 100                 -- Excluye valores bajos
  AND "fecha" = '2024-08'                     -- Muy restrictivo
  AND "descripcion_especialidad" = 'Cardiología'                  -- Muy específico
GROUP BY "nombre_medico"
HAVING COUNT(*) > 50;                  -- Muy restrictivo
"

🔴 **ESTRATEGIA ANTI-CONSULTAS VACÍAS**:

1. **COMENZAR SIMPLE Y EXPANDIR**:
   - Primer intento: Solo filtros esenciales (fecha, IS NOT NULL)
   - Si hay datos: Agregar filtros específicos
   - Si no hay datos: Expandir criterios

2. **INCLUIR CAMPOS DE VERIFICACIÓN**:
   - Siempre incluir COUNT(*) AS total_registros
   - Incluir COUNT(DISTINCT "descripcion_especialidad") para variedad
   - Mostrar MIN/MAX de "fecha" y SUM("estudios")

3. **FALLBACK AUTOMÁTICO**:
   - Si búsqueda específica falla, usar búsqueda general
   - Expandir período de tiempo automáticamente
   - Relajar filtros de especialidad/centro si es necesario

REGLAS CRÍTICAS DE SINTAXIS PARA DATOS MÉDICOS:
1. TODOS los nombres de columna DEBEN ir entre comillas dobles: "fecha", "nombre_medico", "descripcion_especialidad", "centro_sanitario", etc.
2. Para filtros por fecha: WHERE "fecha" = '2024-08' o WHERE "fecha" BETWEEN '2024-01' AND '2024-12'
3. Para conversión de tipos en ventas_sucursal: CAST("estudios" AS INTEGER), CAST("importe" AS NUMERIC)
4. Para operaciones aritméticas: CAST("estudios" AS NUMERIC) o CAST("importe" AS NUMERIC)
5. Para análisis de productividad: SUM("estudios") / (SELECT SUM("estudios") FROM tabla WHERE [condiciones]) * 100
6. Verifica que todas las estructuras de control (CASE, WITH, subqueries) estén correctamente cerradas.
7. Cada declaración CASE debe tener al menos un WHEN y terminar con END.
8. Para prevenir división por cero: CAST(numerador AS NUMERIC) / NULLIF(CAST(denominador AS NUMERIC), 0)
9. Antes de finalizar, verifica que la consulta esté completa sintácticamente y no termine abruptamente.

IMPORTANTES REGLAS ESPECÍFICAS PARA DATOS MÉDICOS:
1. Dos tablas disponibles: ventas_medico (análisis por médico) y ventas_sucursal (análisis por prestación)
2. Todos los nombres de columna van entre comillas dobles por contener espacios
3. Los análisis típicos incluyen: productividad por médico, análisis por especialidad, comparación por centro sanitario
4. Para análisis temporales usar "fecha" en formato 'AAAA-MM'
5. Para análisis de productividad usar "estudios" e "importe"
6. "tipo_pac2" permite análisis por tipo de paciente: WHERE "tipo_pac2" = 'externos' o WHERE "tipo_pac2" = 'internos'
7. "centro_sanitario" permite análisis por ubicación/sucursal
8. Para consultas sobre especialidades específicas usar: "descripcion_especialidad" ILIKE '%especialidad%'
9. Para consultas sobre prestaciones usar tabla ventas_sucursal con "descripcion_posicion"
10. La consulta resultante debe ser una sola consulta SQL válida para PostgreSQL adaptada al sector salud.
11. No incluyas comentarios con # o -- ni explicaciones, solo la consulta SQL pura.
12. SIEMPRE incluir filtros temporales amplios para maximizar datos disponibles.
13. SIEMPRE usar filtros inclusivos (>=) en lugar de exclusivos (>) para "estudios" e "importe".
14. Usar HAVING muy flexible (>= 0) para garantizar inclusión de datos.
15. Usar búsquedas ILIKE '%texto%' para campos de texto como especialidades y nombres.
16. Procura que tu consulta sea adaptada para presentarse como resultado en excel con datos del sector salud.
17. Siempre sé explícito en el tipo de datos en todas las operaciones.
18. Si consideras que la consulta es abstracta sin datos específicos que mostrar, entonces regresa un SELECT mostrando las columnas de las tablas con algunos ejemplos.
19. **PRIORIZA SIEMPRE QUE LA CONSULTA TRAIGA DATOS del sector salud, aunque sea menos específica**.

⚠️ FORMATO DE RESPUESTA:
- Devuelve ÚNICAMENTE el código SQL puro
- NO incluyas comentarios, títulos o explicaciones
- NO uses formato markdown
- El SQL debe empezar directamente con SELECT, WITH, etc.
- Termina con punto y coma
- DEBE incluir filtros temporales amplios y filtros inclusivos para garantizar datos del sector salud
`; }